// src/components/programs/ProgramForm/Step1Sections/DayItinerary/DayCard.jsx

import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    IconButton,
    Collapse,
    Divider,
    alpha,
    useTheme,
    TextField,
    Stack,
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import ProductInstanceCard from "./ProductInstanceCard";
import {
    DRAG_TYPES,
    createProductInstance,
    cloneProductInstance,
} from "../utils/DragDropContext";
import toast from "react-hot-toast";

const DayCard = ({
    day,
    dayIndex,
    onRemoveProduct,
    onReorderProducts,
    onUpdateDayName,
    onUpdateProductField,
    onAddProduct,
    errors,
    isArabic,
    activeLang,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isExpanded, setIsExpanded] = useState(true);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const draggedIndexRef = React.useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    // ✅ تحديث: تتبع عمق الـ drag لاستقبال المنتجات في أي مكان ضمن الكارد
    const [dragDepth, setDragDepth] = React.useState(0);

    // ✅ Local state للتحكم بالتعديل
    const [isEditingAr, setIsEditingAr] = useState(false);
    const [isEditingEn, setIsEditingEn] = useState(false);
    const [tempNameAr, setTempNameAr] = useState('');
    const [tempNameEn, setTempNameEn] = useState('');

    // Get day name with default values
    const defaultNameAr = `اليوم ${dayIndex + 1}`;
    const defaultNameEn = `Day ${dayIndex + 1}`;

    const dayNameAr = day.day_name?.ar || defaultNameAr;
    const dayNameEn = day.day_name?.en || defaultNameEn;

    useEffect(() => {
        const needsDefaultAr = !day.day_name?.ar;
        const needsDefaultEn = !day.day_name?.en;

        if (needsDefaultAr && needsDefaultEn) {
            onUpdateDayName(dayIndex, 'ar', defaultNameAr);
            onUpdateDayName(dayIndex, 'en', defaultNameEn);
        } else {
            if (needsDefaultAr) {
                onUpdateDayName(dayIndex, 'ar', defaultNameAr);
            }
            if (needsDefaultEn) {
                onUpdateDayName(dayIndex, 'en', defaultNameEn);
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * ✅ Handle focus - بداية التعديل
     */
    const handleFocusAr = () => {
        setIsEditingAr(true);
        setTempNameAr(dayNameAr === defaultNameAr ? '' : dayNameAr);
    };

    const handleFocusEn = () => {
        setIsEditingEn(true);
        setTempNameEn(dayNameEn === defaultNameEn ? '' : dayNameEn);
    };

    /**
     * ✅ Handle blur - حفظ التعديل
     */
    const handleBlurAr = () => {
        setIsEditingAr(false);
        const finalValue = tempNameAr.trim() || defaultNameAr;
        onUpdateDayName(dayIndex, 'ar', finalValue);
    };

    const handleBlurEn = () => {
        setIsEditingEn(false);
        const finalValue = tempNameEn.trim() || defaultNameEn;
        onUpdateDayName(dayIndex, 'en', finalValue);
    };

    // ✅ منع drag and drop عندما تكون الماوس فوق TextField
    const handleTextFieldPointerDown = (e) => {
        e.stopPropagation();
    };

    /**
     * ✅ مهمة 4: التحقق من البيانات الناقصة في المنتجات
     */
    const hasIncompleteProducts = () => {
        return day.products?.some(product => {
            const pricingCode = product.product_snapshot?.pricing_unit;

            // Check based on pricing type
            if (pricingCode === "per_person") {
                return !product.dynamic_fields?.pickup_time || !product.dynamic_fields?.city;
            } else if (pricingCode === "per_night") {
                return !product.dynamic_fields?.nights;
            } else if (pricingCode === "per_unit") {
                return !product.dynamic_fields?.time || !product.dynamic_fields?.units;
            }
            return false;
        });
    };

    /**
     * ✅ Handle drag start (for reordering within same day)
     * يضع البيانات في text/plain لتمييزها عن المنتجات الجديدة
     */
    const handleProductDragStart = (e, index) => {
        setDraggedIndex(index);
        draggedIndexRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', `internal-${dayIndex}-${index}`);
        e.stopPropagation(); // منع بت للـ parent
    };

    const handleProductDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
        draggedIndexRef.current = null;
    };

    /**
     * ✅ عند drag over منتج - قد يكون internal أو external
     * التحقق من الـ text/plain للتمييز بين الحالتين
     */
    const handleProductDragOver = (e, index) => {
        e.preventDefault();
        e.stopPropagation();

        const plainData = e.dataTransfer.getData('text/plain');
        
        // إذا كانت بيانات internal (reorder):
        if (plainData && plainData.startsWith(`internal-${dayIndex}`)) {
            e.dataTransfer.dropEffect = 'move';
            const currentDragged = draggedIndexRef.current;
            if (currentDragged !== null && currentDragged !== index) {
                setDragOverIndex(index);
            }
            return;
        }

        // إذا لم تكن بيانات internal - قد تكون external (منتج جديد)
        // دع الـ dragDepth يتعامل معها
        const jsonData = e.dataTransfer.getData('application/json');
        if (jsonData) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    /**
     * ✅ عند drop على منتج - تحقق من نوع البيانات
     */
    const handleProductDrop = (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();

        const plainData = e.dataTransfer.getData('text/plain');
        
        // إذا كانت بيانات internal - reorder
        if (plainData && plainData.startsWith(`internal-${dayIndex}`)) {
            const currentDragged = draggedIndexRef.current;
            if (currentDragged !== null && currentDragged !== dropIndex) {
                const newProducts = [...day.products];
                const [draggedProduct] = newProducts.splice(currentDragged, 1);

                // تعديل dropIndex إذا كان الـ source قبل الـ target
                const adjustedIndex = currentDragged < dropIndex ? dropIndex - 1 : dropIndex;
                newProducts.splice(adjustedIndex, 0, draggedProduct);

                const reorderedProducts = newProducts.map((p, idx) => ({
                    ...p,
                    order: idx + 1,
                }));

                onReorderProducts(dayIndex, reorderedProducts);
                toast.success(
                    isArabic
                        ? 'تم تغيير الترتيب بنجاح'
                        : 'Order changed successfully'
                );
            }
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        // إذا لم تكن internal - دع handleDayDrop تتعامل معها
        // (لا تفعل شيء هنا - الـ external drag يجب أن يتم على المساحة الفارغة)
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    /**
     * ✅ معالجات Day-level - فقط للمنتجات الخارجية (external drag)
     * البيانات الداخلية يتم معالجتها على مستوى ProductInstanceCard
     */
    const handleDayDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const plainData = e.dataTransfer.getData('text/plain');
        const jsonData = e.dataTransfer.getData('application/json');
        
        // تجاهل إعادة الترتيب الداخلية (لا تملك application/json)
        if (plainData && plainData.startsWith('internal-')) {
            return;
        }

        // للمنتجات الجديدة فقط (تحتوي على application/json)
        if (jsonData) {
            setDragDepth(prev => prev + 1);
        }
    };

    const handleDayDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const plainData = e.dataTransfer.getData('text/plain');
        const jsonData = e.dataTransfer.getData('application/json');
        
        // تجاهل إعادة الترتيب الداخلية
        if (plainData && plainData.startsWith('internal-')) {
            return;
        }

        // للمنتجات الجديدة فقط
        if (jsonData) {
            setDragDepth(prev => Math.max(0, prev - 1));
        }
    };

    const handleDayDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const plainData = e.dataTransfer.getData('text/plain');
        const jsonData = e.dataTransfer.getData('application/json');
        
        // تجاهل إعادة الترتيب الداخلية
        if (plainData && plainData.startsWith('internal-')) {
            return;
        }

        // للمنتجات الجديدة فقط
        if (jsonData) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    // ✅ Product refs للـ internal reorder
    const productRefs = React.useRef([]);

    const handleDayDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // reset drag depth
        setDragDepth(0);

        const plainData = e.dataTransfer.getData('text/plain');
        const jsonData = e.dataTransfer.getData('application/json');

        /**
         * ✅ الحالة 1: Internal reorder (تم on a product card)
         * لا يجب أن نصل هنا - يتم التعامل في handleProductDrop
         * لكن في حالة drop على المساحة الفارغة بين المنتجات:
         */
        if (plainData && plainData.startsWith('internal-')) {
            const parts = plainData.split('-');
            const srcDay = parseInt(parts[1], 10);
            const srcIndex = parseInt(parts[2], 10);

            // تجاهل إعادة الترتيب في نفس اليوم (يتم في handleProductDrop)
            if (!isNaN(srcDay) && !isNaN(srcIndex) && srcDay === dayIndex) {
                // تم معالجتها بالفعل في handleProductDrop
                return;
            }
            // إذا كانت من يوم آخر - تجاهلها
            return;
        }

        /**
         * ✅ الحالة 2: External drag (منتج جديد من الـ panel)
         * البيانات موجودة في application/json
         */
        if (jsonData) {
            try {
                const dragData = JSON.parse(jsonData);
                if (!dragData || !dragData.type) return;

                switch (dragData.type) {
                    case DRAG_TYPES.PRODUCT_NEW:
                        handleAddNewProduct(dragData.product);
                        break;

                    case DRAG_TYPES.PRODUCT_EXISTING:
                        handleCopyProduct(dragData.product, dragData.sourceDayIndex);
                        break;

                    default:
                        break;
                }
            } catch (error) {
                console.error('Error parsing drag data:', error);
            }
        }
    };

    const handleAddNewProduct = (product) => {
        const isDuplicate = day.products.some(
            (p) => p.product_snapshot.product_id === product.id
        );

        if (isDuplicate) {
            toast.error(
                isArabic
                    ? 'المنتج موجود بالفعل في هذا اليوم'
                    : 'Product already exists in this day'
            );
            return;
        }

        const instance = createProductInstance(product, day.products.length + 1);

        if (onAddProduct) {
            onAddProduct(dayIndex, instance);
        }

        toast.success(
            isArabic
                ? 'تمت إضافة المنتج بنجاح'
                : 'Product added successfully'
        );
    };

    const handleCopyProduct = (product, sourceDayIndex) => {
        if (sourceDayIndex === dayIndex) {
            toast.info(
                isArabic
                    ? 'المنتج موجود بالفعل في هذا اليوم'
                    : 'Product already in this day'
            );
            return;
        }

        const isDuplicate = day.products.some(
            (p) => p.product_snapshot.product_id === product.product_snapshot.product_id
        );

        if (isDuplicate) {
            toast.warning(
                isArabic
                    ? 'المنتج موجود بالفعل في هذا اليوم'
                    : 'Product already exists in this day'
            );
            return;
        }

        const instance = cloneProductInstance(product, day.products.length + 1);

        if (onAddProduct) {
            onAddProduct(dayIndex, instance);
        }

        toast.success(
            isArabic
                ? 'تم نسخ المنتج بنجاح'
                : 'Product copied successfully'
        );
    };

    // ✅ إذا كان dragDepth > 0، فهذا يعني أن المنتج داخل المنطقة
    const isDropZoneVisuallyActive = dragDepth > 0;

    // ✅ مهمة 4: تحديد لون Border بناءً على البيانات الناقصة
    const hasIncompleteData = hasIncompleteProducts();

    return (
        <Card
            onDragEnter={handleDayDragEnter}
            onDragLeave={handleDayDragLeave}
            onDragOver={handleDayDragOver}
            onDrop={handleDayDrop}
            elevation={isDropZoneVisuallyActive ? 4 : 1}
            sx={{
                mb: 2,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                // ✅ مهمة 4: Border أحمر للبيانات الناقصة، أزرق منقط للـ drag zone
                border: hasIncompleteData
                    ? `2px solid ${theme.palette.error.main}`
                    : isDropZoneVisuallyActive
                        ? `2px dashed ${theme.palette.primary.main}`
                        : `1px solid ${alpha(isDark ? '#FFFFFF' : '#000000', 0.08)}`,
                bgcolor: isDropZoneVisuallyActive
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'background.paper',
                position: 'relative',
                overflow: 'visible',
                '&:hover': {
                    boxShadow: 2,
                    borderColor: hasIncompleteData
                        ? theme.palette.error.main
                        : alpha(theme.palette.primary.main, 0.3),
                },
            }}
        >
            {isDropZoneVisuallyActive && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        zIndex: 10,
                        pointerEvents: 'none',
                        borderRadius: 'inherit',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="primary.main"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <span>⬇️</span>
                        {isArabic ? 'أفلت المنتج هنا' : 'Drop Product Here'}
                    </Typography>
                </Box>
            )}

            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    {/* Day Number */}
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            border: `2px solid ${theme.palette.primary.main}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '1rem',
                            color: 'primary.main',
                            flexShrink: 0,
                        }}
                    >
                        {dayIndex + 1}
                    </Box>

                    {/* ✅ Editable Names with Hints */}
                    <Stack
                        spacing={0.75}
                        sx={{ flex: 1, minWidth: 0 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{
                            display: 'flex', alignItems: 'center',
                            gap: { md: 4, xs: 1 }, flexDirection: { md: 'row', xs: 'column' }
                        }}>
                            {/* Arabic Name */}
                            <TextField
                                size="small"
                                value={isEditingAr ? tempNameAr : dayNameAr}
                                onChange={(e) => setTempNameAr(e.target.value)}
                                onFocus={handleFocusAr}
                                onBlur={handleBlurAr}
                                onPointerDown={handleTextFieldPointerDown}
                                placeholder="اكتب اسم اليوم بالعربية..."
                                fullWidth
                                variant="standard"
                                InputProps={{
                                    startAdornment: (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                mr: 0.5,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            AR
                                        </Typography>
                                    ),
                                    sx: {
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                        '&:before': {
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        },
                                        '&:hover:not(.Mui-disabled):before': {
                                            borderBottom: `1.5px solid ${theme.palette.primary.main}`,
                                        },
                                    }
                                }}
                                sx={{
                                    '& .MuiInput-input': {
                                        padding: '2px 0',
                                        '::placeholder': {
                                            color: theme.palette.text.secondary,
                                            opacity: 0.6,
                                            fontSize: '0.85rem',
                                        }
                                    }
                                }}
                            />

                            {/* English Name */}
                            <TextField
                                size="small"
                                value={isEditingEn ? tempNameEn : dayNameEn}
                                onChange={(e) => setTempNameEn(e.target.value)}
                                onFocus={handleFocusEn}
                                onBlur={handleBlurEn}
                                onPointerDown={handleTextFieldPointerDown}
                                placeholder="Type day name in English..."
                                fullWidth
                                variant="standard"
                                InputProps={{
                                    startAdornment: (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                mr: 0.5,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            EN
                                        </Typography>
                                    ),
                                    sx: {
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        '&:before': {
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        },
                                        '&:hover:not(.Mui-disabled):before': {
                                            borderBottom: `1.5px solid ${theme.palette.primary.main}`,
                                        },
                                    }
                                }}
                                sx={{
                                    '& .MuiInput-input': {
                                        padding: '2px 0',
                                        '::placeholder': {
                                            color: theme.palette.text.secondary,
                                            opacity: 0.6,
                                            fontSize: '0.8rem',
                                        }
                                    }
                                }}
                            />
                        </Box>

                        {/* Date Badge */}
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                            📅 {day.date}
                        </Typography>
                    </Stack>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                        <Chip
                            label={day.products?.length || 0}
                            color={day.products?.length > 0 ? 'primary' : 'default'}
                            size="small"
                            sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                minWidth: 32,
                            }}
                        />

                        {day.products?.length > 0 && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(!isExpanded);
                                }}
                                sx={{
                                    width: 28,
                                    height: 28,
                                }}
                            >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Empty State */}
                {day.products?.length === 0 && (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 2,
                            px: 2,
                            borderRadius: 1.5,
                            border: `1.5px dashed ${alpha(theme.palette.divider, 0.4)}`,
                            bgcolor: alpha(theme.palette.action.hover, 0.2),
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                            {isArabic
                                ? '🎯 اسحب المنتجات هنا'
                                : '🎯 Drag products here'}
                        </Typography>
                    </Box>
                )}

                {/* Products List - استقبال المنتجات الجديدة في أي مكان ضمن المنتجات */}
                {day.products?.length > 0 && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Collapse in={isExpanded} timeout="auto">
                            {/* ✅ حاوية استقبال المنتجات الجديدة تحيط بقائمة المنتجات */}
                            <Box
                                onDragEnter={handleDayDragEnter}
                                onDragLeave={handleDayDragLeave}
                                onDragOver={handleDayDragOver}
                                onDrop={handleDayDrop}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    position: 'relative',
                                    borderRadius: 1,
                                    transition: 'all 0.2s ease',
                                    // ✅ منطقة حساسة للـ drag
                                    pointerEvents: 'auto',
                                }}
                            >
                                {day.products.map((product, productIndex) => (
                                    <Box
                                        key={product.instance_id}
                                        ref={(el) => (productRefs.current[productIndex] = el)}
                                    >
                                        <ProductInstanceCard
                                            product={product}
                                            productIndex={productIndex}
                                            dayIndex={dayIndex}
                                            onRemove={() => onRemoveProduct(dayIndex, product.instance_id)}
                                            onUpdateField={(field, value) =>
                                                onUpdateProductField(dayIndex, product.instance_id, field, value)
                                            }
                                            onDragStart={(e) => handleProductDragStart(e, productIndex)}
                                            onDragEnd={handleProductDragEnd}
                                            onDragOver={(e) => handleProductDragOver(e, productIndex)}
                                            onDrop={(e) => handleProductDrop(e, productIndex)}
                                            isDragging={draggedIndex === productIndex}
                                            isDragOver={dragOverIndex === productIndex}
                                            errors={errors}
                                            isArabic={isArabic}
                                            activeLang={activeLang}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default DayCard;
